import { useEffect, useState } from "react";
import { useJoinCompetitionMutation } from "@/features/predictions/hooks/useJoinCompetitionMutation";
import { useMyPronosticsPreferences } from "@/features/predictions/hooks/useMyPronosticsPreferences";
import { PRONOSTICS_ONBOARDING_KEY } from "@/features/predictions/hooks/usePronosticsOnboarding";
import { storageService } from "@/shared/services/localStorage.service";

export const PRONOSTICS_OPTED_OUT_MODAL_KEY = "pronostics_opted_out_modal_seen";

const readSeen = (): boolean => {
  try {
    return storageService.getItem(PRONOSTICS_OPTED_OUT_MODAL_KEY) === "true";
  }
  catch {
    return true;
  }
};

const readOnboardingCompleted = (): boolean => {
  try {
    return storageService.getItem(PRONOSTICS_ONBOARDING_KEY) === "true";
  }
  catch {
    return false;
  }
};

export const usePronosticsOptedOutModal = () => {
  const { data: preferences } = useMyPronosticsPreferences();
  const joinMutation = useJoinCompetitionMutation();
  const [shouldShow, setShouldShow] = useState<boolean>(
    () => !readSeen() && readOnboardingCompleted(),
  );

  // When the user rejoins the competition, reset the seen flag so the modal
  // will reappear if they later opt out again (one shot per opt-out cycle).
  useEffect(() => {
    if (preferences?.isParticipating === true) {
      try {
        storageService.removeItem(PRONOSTICS_OPTED_OUT_MODAL_KEY);
      }
      catch {
        // ignore storage errors
      }
      setShouldShow(true);
    }
  }, [preferences?.isParticipating]);

  const isOpen = shouldShow && preferences?.isParticipating === false;

  const dismiss = () => {
    try {
      storageService.setItem(PRONOSTICS_OPTED_OUT_MODAL_KEY, "true");
    }
    catch {
      // ignore storage errors, still close the modal
    }
    setShouldShow(false);
  };

  const join = () => {
    joinMutation.mutate(undefined, {
      onSuccess: () => {
        setShouldShow(false);
      },
    });
  };

  return {
    isOpen,
    dismiss,
    join,
    isJoining: joinMutation.isPending,
  };
};
